import type { OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  Input,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import type { Applications } from '@freelancer/config';
import { APPS_DOMAINS_MAP, AppsDomainsMap } from '@freelancer/config';
import { Datastore } from '@freelancer/datastore';
import type { LanguagesCollection } from '@freelancer/datastore/collections';
import { Localization } from '@freelancer/localization';
import { ButtonColor } from '@freelancer/ui/button';
import { IconSize } from '@freelancer/ui/icon';
import type { SelectItem } from '@freelancer/ui/select';
import { FontColor, FontWeight, TextSize } from '@freelancer/ui/text';
import { UI_CONFIG } from '@freelancer/ui/ui.config';
import { UiConfig } from '@freelancer/ui/ui.interface';
import type { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { LanguageSwitcher } from './language-switcher.service';

@Component({
  standalone: false,
  selector: 'fl-language-switcher-inline',
  template: `
    <div class="Language">
      <fl-icon
        class="mr-xsmall mobile:show"
        [name]="'ui-globe'"
        [useIconFont]="true"
        [size]="IconSize.SMALL"
      ></fl-icon>
      <p
        class="mr-xxsmall tablet:mr-xxxsmall"
        i18n="Language navigation menu label"
      >
        Language
      </p>
      @if (!compact) {
        <div class="mb-xxxsmall tablet:mb-xxsmall">
          <fl-label
            i18n="Language switcher label"
            for="language-select"
            [weight]="FontWeight.BOLD"
            [size]="TextSize.INHERIT"
            [sizeTablet]="TextSize.SMALL"
            [color]="FontColor.LIGHT"
          >
            Language
          </fl-label>
        </div>
      }
      <fl-select
        class="Language-select"
        [control]="languageControl"
        [options]="languages$ | flAsync"
      >
      </fl-select>
    </div>
    @if (languageControl.dirty) {
      <fl-button
        class="Language-button"
        i18n="Language switcher button (verb)"
        [color]="ButtonColor.SECONDARY"
        (click)="handleUpdate()"
      >
        Update
      </fl-button>
    }
  `,
  styleUrls: ['./language-switcher-inline.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LanguageSwitcherInlineComponent implements OnInit {
  ButtonColor = ButtonColor;
  FontColor = FontColor;
  FontWeight = FontWeight;
  IconSize = IconSize;
  TextSize = TextSize;

  languageControl = new FormControl(this.localization.languageCode, {
    nonNullable: true,
  });

  languages$: Observable<readonly SelectItem[]>;

  @Input() compact = false;

  constructor(
    private localization: Localization,
    private datastore: Datastore,
    private languageSwitcher: LanguageSwitcher,
    @Inject(APPS_DOMAINS_MAP) private appsDomainsMap: AppsDomainsMap,
    @Inject(UI_CONFIG) private uiConfig: UiConfig,
  ) {}

  ngOnInit(): void {
    // TODO: T314332 - Casting is required as `uiConfig.theme` still contains 'deloitte' while deloitte has been removed from Applications.
    // This should be cleaned up after assessing the the likelihood of Deloitte's resurrection
    const languageCodes = Object.keys(
      this.appsDomainsMap[(this.uiConfig.theme as Applications) ?? 'main'],
    ).map(
      // Remove -CUSTOM suffix to support flexible terminology
      code => code.replace('-CUSTOM', ''),
    );

    this.languages$ = this.datastore
      .collection<LanguagesCollection>('languages', query =>
        query.where('code', 'in', languageCodes),
      )
      .valueChanges()
      .pipe(
        map(languages =>
          languages.map(language => ({
            value: language.code,
            displayText: language.name,
          })),
        ),
      );
  }

  handleUpdate(): void {
    this.languageSwitcher.switchLanguage(this.languageControl.value);
  }
}
